import React, { useState, useEffect, useCallback } from 'react';
import { Card, Statistic, Table, Spin, Empty, Tag, Tabs } from 'antd';
import {
  EyeOutlined, GlobalOutlined, SearchOutlined,
  CalendarOutlined, RiseOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { backendURL } from '../Globals';
import Cookies from 'js-cookie';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

// ─── Stat card ────────────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon, color }) => (
  <Card
    bordered={false}
    style={{ borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderLeft: `4px solid ${color}` }}
    bodyStyle={{ padding: '18px 24px' }}
  >
    <Statistic
      title={<span style={{ fontSize: 12, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</span>}
      value={value ?? '—'}
      prefix={React.cloneElement(icon, { style: { color, marginRight: 4 } })}
      valueStyle={{ fontSize: 32, fontWeight: 800, color: '#1a1a1a', fontFamily: "'Inter','Segoe UI',sans-serif" }}
    />
  </Card>
);

// ─── Component ────────────────────────────────────────────────────────────────

const UsageComponent = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiKey = Cookies.get('apiKey') || localStorage.getItem('apiKey');
      const res = await fetch(`${backendURL}/usage/stats`, {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      if (!res.ok) throw new Error(res.status === 401 ? 'No autorizado' : 'Error del servidor');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── Visits bar chart (últimos 30 días) ──────────────────────────────────────

  const visitChartOption = data ? (() => {
    const raw   = data.visits.daily || [];
    const dates = raw.map(d => fmtDate(d.date));
    const vals  = raw.map(d => d.visits);
    return {
      tooltip:  { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid:     { left: 40, right: 20, top: 20, bottom: 40 },
      xAxis:    { type: 'category', data: dates, axisLabel: { fontSize: 10, rotate: 35, interval: 'auto' } },
      yAxis:    { type: 'value', minInterval: 1 },
      series:   [{ type: 'bar', data: vals, itemStyle: { color: '#007398', borderRadius: [3,3,0,0] }, barMaxWidth: 32 }],
    };
  })() : null;

  // ── Cities bar chart ────────────────────────────────────────────────────────

  const cityChartOption = data ? (() => {
    const cities = [...(data.visits.topCities || [])].reverse();
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid:    { left: 110, right: 20, top: 10, bottom: 20 },
      xAxis:   { type: 'value', minInterval: 1 },
      yAxis:   { type: 'category', data: cities.map(c => c.city || '(desconocida)'), axisLabel: { fontSize: 11 } },
      series:  [{ type: 'bar', data: cities.map(c => c.visits), itemStyle: { color: '#5470c6', borderRadius: [0,3,3,0] }, barMaxWidth: 24 }],
    };
  })() : null;

  // ── Search columns ──────────────────────────────────────────────────────────

  const searchCols = [
    { title: '#', key: 'rank', width: 50, render: (_, __, i) => <span style={{ color: '#bbb', fontSize: 12 }}>{i + 1}</span> },
    {
      title: 'Consulta', dataIndex: 'query',
      render: q => <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{q}</span>,
    },
    {
      title: 'Veces', dataIndex: 'count', width: 80, align: 'center',
      render: n => <Tag color="blue" style={{ fontWeight: 700 }}>{n}</Tag>,
    },
    {
      title: 'Última búsqueda', dataIndex: 'last_search', width: 150,
      render: d => <span style={{ fontSize: 12, color: '#999' }}>{fmtDateTime(d)}</span>,
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
      <Spin size="large" tip="Cargando estadísticas…" />
    </div>
  );

  if (error) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Empty description={<span style={{ color: '#e74c3c' }}>Error: {error}</span>} />
    </div>
  );

  const { visits, searches } = data;

  return (
    <div style={{ padding: '32px 40px 60px', maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Título ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, margin: 0, color: '#1a1a1a', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
          Uso de la web
        </h2>
        <p style={{ color: '#888', marginTop: 6, fontSize: 14 }}>
          Accesos únicos (ventana de sesión: 30 min) · Búsquedas registradas
        </p>
      </div>

      {/* ── Stat cards ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 16, marginBottom: 36 }}>
        <StatCard title="Total visitas únicas"  value={visits.total}   icon={<EyeOutlined />}      color="#007398" />
        <StatCard title="Hoy"                   value={visits.today}   icon={<CalendarOutlined />}  color="#52c41a" />
        <StatCard title="Últimos 7 días"         value={visits.week}    icon={<RiseOutlined />}      color="#fa8c16" />
        <StatCard title="Últimos 30 días"        value={visits.month}   icon={<CalendarOutlined />}  color="#722ed1" />
        <StatCard title="Total búsquedas"        value={searches.total} icon={<SearchOutlined />}    color="#eb2f96" />
      </div>

      {/* ── Charts ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 36 }}>

        {/* Visitas por día */}
        <Card
          bordered={false}
          style={{ borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}
          bodyStyle={{ padding: '20px 20px 12px' }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Visitas únicas por día (últimos 30 días)
          </div>
          {visits.daily?.length > 0
            ? <ReactECharts option={visitChartOption} style={{ height: 220 }} opts={{ renderer: 'svg' }} notMerge />
            : <Empty description="Sin datos aún" style={{ padding: 40 }} />
          }
        </Card>

        {/* Ciudades */}
        <Card
          bordered={false}
          style={{ borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}
          bodyStyle={{ padding: '20px 20px 12px' }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
            <GlobalOutlined /> Top ciudades
          </div>
          {visits.topCities?.length > 0
            ? <ReactECharts option={cityChartOption} style={{ height: 220 }} opts={{ renderer: 'svg' }} notMerge />
            : <Empty description="Sin geolocalización" style={{ padding: 40 }} />
          }
        </Card>
      </div>

      {/* ── Búsquedas ──────────────────────────────────────────────────────── */}
      <Card
        bordered={false}
        style={{ borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}
        bodyStyle={{ padding: 0 }}
      >
        <Tabs
          defaultActiveKey="trans"
          style={{ padding: '0 20px' }}
          tabBarStyle={{ fontWeight: 600 }}
          items={[
            {
              key: 'trans',
              label: (
                <span><SearchOutlined style={{ marginRight: 6 }} />Transformativos ({searches.transformativos?.length ?? 0})</span>
              ),
              children: (
                <Table
                  dataSource={searches.transformativos || []}
                  columns={searchCols}
                  rowKey={(r, i) => i}
                  size="small"
                  pagination={{ pageSize: 25, showSizeChanger: false }}
                  locale={{ emptyText: 'Sin búsquedas registradas' }}
                  style={{ padding: '0 0 16px' }}
                />
              ),
            },
            {
              key: 'dia',
              label: (
                <span><SearchOutlined style={{ marginRight: 6 }} />Diamante ({searches.diamante?.length ?? 0})</span>
              ),
              children: (
                <Table
                  dataSource={searches.diamante || []}
                  columns={searchCols}
                  rowKey={(r, i) => i}
                  size="small"
                  pagination={{ pageSize: 25, showSizeChanger: false }}
                  locale={{ emptyText: 'Sin búsquedas registradas' }}
                  style={{ padding: '0 0 16px' }}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default UsageComponent;
